import { DeliveryStatus, OrderStatus } from '@instamenu/data-access';
import { palette } from '@instamenu/ui-vanilla-extract';
import { LanguageTranslation } from '@instamenu/context-i18n';
import { Clock, ChefHat, Truck, Store, House, CheckCircle2 } from 'lucide-react';

type StatusInfo = { label: string; description: string; isError: boolean; i18nKey: StatusI18nKey };

type StatusKey = keyof LanguageTranslation['status'];
type StatusI18nKey = `status.${StatusKey}`;

const ORDER_STATUS_MAP: Partial<Record<OrderStatus, StatusInfo>> = {
  [OrderStatus.Created]: {
    label: 'Created',
    description: 'Waiting for the restaurant to accept your order',
    isError: false,
    i18nKey: 'status.created',
  },
  [OrderStatus.Placed]: {
    label: 'Placed',
    description: 'Waiting for the restaurant to accept your order',
    isError: false,
    i18nKey: 'status.placed',
  },
  [OrderStatus.Accepted]: {
    label: 'Accepted',
    description: 'Order accepted',
    isError: false,
    i18nKey: 'status.accepted',
  },
  [OrderStatus.Completed]: {
    label: 'Completed',
    description: 'Order completed',
    isError: false,
    i18nKey: 'status.completed',
  },
  [OrderStatus.Canceled]: {
    label: 'Canceled',
    description: 'The restaurant did not accept this order. You have been refunded.',
    isError: true,
    i18nKey: 'status.canceled',
  },
};

const DELIVERY_STATUS_MAP: Record<DeliveryStatus, StatusInfo> = {
  [DeliveryStatus.Submitted]: {
    label: 'Submitted',
    description: 'Preparing your food',
    isError: false,
    i18nKey: 'status.submitted',
  },
  [DeliveryStatus.Accepted]: {
    label: 'Driver Accepted',
    description: 'A driver has accepted your order',
    isError: false,
    i18nKey: 'status.deliveryAccepted',
  },
  [DeliveryStatus.PickupEnroute]: {
    label: 'Heading to Pickup',
    description: 'The driver is on the way to pick up your food',
    isError: false,
    i18nKey: 'status.pickupEnroute',
  },
  [DeliveryStatus.AtPickup]: {
    label: 'At Pickup',
    description: 'The driver is at the restaurant waiting for your order',
    isError: false,
    i18nKey: 'status.atPickup',
  },
  [DeliveryStatus.DropoffEnroute]: {
    label: 'On the way',
    description: 'Your order is on its way to you',
    isError: false,
    i18nKey: 'status.dropoffEnroute',
  },
  [DeliveryStatus.Delivered]: {
    label: 'Delivered',
    description: 'Your order has been delivered successfully',
    isError: false,
    i18nKey: 'status.delivered',
  },
  [DeliveryStatus.DeliveryProblem]: {
    label: 'Delivery Problem',
    description: 'There was an issue with your delivery. Please contact support.',
    isError: true,
    i18nKey: 'status.deliveryProblem',
  },
  [DeliveryStatus.Returned]: {
    label: 'Order Returned',
    description: 'Your order has been returned to the restaurant.',
    isError: true,
    i18nKey: 'status.returned',
  },
  [DeliveryStatus.Canceled]: {
    label: 'Delivery Canceled',
    description: 'Your delivery has been canceled. Please contact support.',
    isError: true,
    i18nKey: 'status.deliveryCanceled',
  },
  [DeliveryStatus.Unknown]: {
    label: 'Status Unknown',
    description: 'We encountered an issue tracking your order. Please contact support.',
    isError: true,
    i18nKey: 'status.unknown',
  },
};

export const getCustomerFacingDeliveryStatus = (deliveryStatus?: DeliveryStatus) =>
  deliveryStatus === DeliveryStatus.Accepted ? DeliveryStatus.PickupEnroute : deliveryStatus;

export const getStepIcon = (
  step: OrderStatus | DeliveryStatus,
  isActive: boolean,
  isCompleted: boolean,
) => {
  // Active + completed indicators are now both filled with the brand
  // color, so their icons need to be white. Upcoming stays neutral gray.
  const iconProps = {
    size: 16,
    strokeWidth: 2,
    color: isCompleted || isActive ? 'white' : palette.gray400,
  };

  switch (step) {
    case OrderStatus.Placed:
      return <Clock {...iconProps} />;
    case DeliveryStatus.Submitted:
      return <ChefHat {...iconProps} />;
    case DeliveryStatus.Accepted:
    case DeliveryStatus.PickupEnroute:
      return <Truck {...iconProps} />;
    case DeliveryStatus.AtPickup:
      // Driver is *at* the restaurant waiting — use the storefront icon so
      // it doesn't read as a duplicate of the PickupEnroute Truck step.
      return <Store {...iconProps} />;
    case DeliveryStatus.DropoffEnroute:
      return <House {...iconProps} />;
    case DeliveryStatus.Delivered:
      return <CheckCircle2 {...iconProps} />;
    default:
      return <Clock {...iconProps} />;
  }
};

export const deliveryStatusMap = (
  orderStatus: OrderStatus,
  deliveryStatus?: DeliveryStatus,
): StatusInfo => {
  const defaultStatus: StatusInfo = {
    label: 'Unknown',
    description: 'Unknown',
    isError: true,
    i18nKey: 'status.unknown',
  };

  if (deliveryStatus) {
    if (DELIVERY_STATUS_MAP[deliveryStatus]) {
      return DELIVERY_STATUS_MAP[deliveryStatus] || defaultStatus;
    }
  }
  return ORDER_STATUS_MAP[orderStatus] || defaultStatus;
};

export const getStatusBackgroundColor = (status: OrderStatus | DeliveryStatus) => {
  const inProgressStatuses = [
    OrderStatus.Placed,
    OrderStatus.Accepted,
    DeliveryStatus.Submitted,
    DeliveryStatus.Accepted,
    DeliveryStatus.PickupEnroute,
    DeliveryStatus.AtPickup,
    DeliveryStatus.DropoffEnroute,
  ];

  const completedStatuses = [OrderStatus.Completed, DeliveryStatus.Delivered];

  const errorStatuses = [
    OrderStatus.Canceled,
    DeliveryStatus.DeliveryProblem,
    DeliveryStatus.Returned,
    DeliveryStatus.Unknown,
    DeliveryStatus.Canceled,
  ];

  if (inProgressStatuses.includes(status)) return palette.teal300;
  if (completedStatuses.includes(status)) return palette.primary;
  if (errorStatuses.includes(status)) return palette.red300;

  return palette.gray100;
};
